import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    crudStatusEnum,
    getCrudStatusLabel,
    getPriceTypeLabel,
    localStorageKeys,
    permissionLevelEnum,
    systemConfigKeyEnum,
} from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateJobMutation } from '../../../../hooks/mutations/useJobMutation';
import { useJobsQuery } from '../../../../hooks/queries/useJobsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { useTheme } from '../../../../hooks/useTheme';
import { Job } from '../../../../services/apiSLTP/jobs';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { formatNumber } from '../../../../utils/numbers';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateJobModal from '../../../modals/admin/internal/UpdateJobModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface JobsSettingsTableProps {
    withUsageCount: boolean;
}

const JobsSettingsTable: React.FC<JobsSettingsTableProps> = ({ withUsageCount }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState<Job | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const { muiTheme } = useTheme();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const { data, isLoading, refetch } = useJobsQuery({ queries: { withUsage: withUsageCount } });

    const { mutate: mutateJob } = useUpdateJobMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating job:', error.message),
    });

    const columnDefs: ColDef<Job>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<Job>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyJob'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedJob(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteJob'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedJob(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            sortable: false,
            filter: false,
            colId: 'actions',
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<Job>) => {
                if (!params.data) return null;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },

        {
            headerName: `${capitalizeFirstLetter(t('price'))} (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
            valueFormatter: (params) => formatNumber(params.value, t('locale')),
            field: 'price',
            cellStyle: (params) => (params.data?.price === 0 ? { color: muiTheme.palette.error.main } : undefined),
            type: 'numericColumn',
            colId: 'price',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => capitalizeFirstLetter(t(getPriceTypeLabel(params.data?.priceType))),
            colId: 'pricing',
        },
        {
            headerName: capitalizeFirstLetter(t('workTravelZones')),
            valueGetter: (params) => !!params.data?.hasTravelZones,
            colId: 'hasTravelZones',
            cellDataType: 'boolean',
            valueFormatter: (params) => (params.value ? capitalizeFirstLetter(t('true')) : capitalizeFirstLetter(t('false'))),
        },
        {
            headerName: capitalizeFirstLetter(t('mealAllowance')),
            valueGetter: (params) => !!params.data?.hasMealAllowance,
            colId: 'hasMealAllowance',
            cellDataType: 'boolean',
            valueFormatter: (params) => (params.value ? capitalizeFirstLetter(t('true')) : capitalizeFirstLetter(t('false'))),
        },
        {
            headerName: capitalizeFirstLetter(t('phoneAllowance')),
            valueGetter: (params) => !!params.data?.hasPhoneAllowance,
            colId: 'hasPhoneAllowance',
            cellDataType: 'boolean',
            valueFormatter: (params) => (params.value ? capitalizeFirstLetter(t('true')) : capitalizeFirstLetter(t('false'))),
        },
        {
            headerName: capitalizeFirstLetter(t('displayOrder')),
            field: 'displayOrder',
            colId: 'displayOrder',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        createDateTimeColumn<Job>('createdAt', 'createdAt'),
        createDateTimeColumn<Job>('updatedAt', 'updatedAt'),
        ...((user?.profileId.permsLevel === permissionLevelEnum.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: 'id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xTotal')),
                      field: 'usageCount.total',
                      colId: 'usageTotal',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xUsers')),
                      field: 'usageCount.users',
                      colId: 'usageUsers',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      filter: 'agNumberColumnFilter',
                  },
              ]
            : []) as ColDef<Job>[]),
    ];

    return (
        <>
            <GenericAgGridTable
                rowData={data || []}
                columnDefs={columnDefs}
                loading={isLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedJob(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addJob'))}
                    />
                }
                exportCsvFileName="admin-jobs"
                storageKey={localStorageKeys.TABLE.ADMIN.INTERNAL.JOBS}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateJobModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    isEditMode={isEditMode}
                    job={selectedJob}
                    refetch={refetch}
                />
            )}

            {showDeleteModal && selectedJob && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => mutateJob({ jobId: selectedJob._id, data: { crudStatus: crudStatusEnum.DELETED } })}
                    title={t('job')}
                    itemName={selectedJob.designation}
                />
            )}
        </>
    );
};

export default JobsSettingsTable;
